import { memo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { FileText, Sparkles, Wrench, Bug, Zap, Rocket, ChevronsLeft, ChevronsRight, History } from 'lucide-react'
import { router } from '@inertiajs/react'
import { restoreHistory } from '@/lib/history-api'
import { withProgressToast } from '@/lib/progress-toast'
import { getDateLocale } from '@/lib/i18n'
import type { HistoryEntry, Pagination } from '@/types/conversations'
import type { TFunction } from 'i18next'

interface HistoryTabProps {
  historyEntries: HistoryEntry[]
  historyPagy?: Pagination
  projectId: number
  deployedHistoryEntryId?: number | null
  processing?: boolean
  isRewinding?: boolean
  onRewindingChange?: (isRewinding: boolean) => void
}

const getEntryTypeConfig = (t: TFunction) => ({
  new_feature: {
    label: t('history.entryTypes.newFeature'),
    icon: Sparkles,
    badgeClass: 'border-blue-200 text-blue-700 bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:bg-blue-900/20'
  },
  enhancement: {
    label: t('history.entryTypes.enhancement'),
    icon: Zap,
    badgeClass: 'border-purple-200 text-purple-700 bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:bg-purple-900/20'
  },
  bug_fix: {
    label: t('history.entryTypes.bugFix'),
    icon: Bug,
    badgeClass: 'border-orange-200 text-orange-700 bg-orange-50 dark:border-orange-800 dark:text-orange-300 dark:bg-orange-900/20'
  },
  refinement: {
    label: t('history.entryTypes.refinement'),
    icon: Wrench,
    badgeClass: 'border-gray-200 text-gray-700 bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:bg-gray-800'
  },
  deployment: {
    label: t('history.entryTypes.deployment'),
    icon: Rocket,
    badgeClass: 'border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-900/20'
  }
})

export const HistoryTab = memo(function HistoryTab({
  historyEntries,
  historyPagy,
  projectId,
  deployedHistoryEntryId,
  processing = false,
  isRewinding = false,
  onRewindingChange
}: HistoryTabProps) {
  const { t, i18n } = useTranslation('conversations')
  const entryTypeConfig = getEntryTypeConfig(t)

  const goToPage = (page: number | null) => {
    if (page !== null) {
      router.get(`/projects/${projectId}`, { history_page: page }, { preserveState: true })
    }
  }

  if (historyEntries.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-gray-900">
        <div className="text-center space-y-4 max-w-md">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <FileText className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-1 text-gray-900 dark:text-gray-100">{t('history.empty.title')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('history.empty.description')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4 bg-white dark:bg-gray-900">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('history.title')}
      </div>

      {historyEntries.map((entry) => {
        const isDeployed = deployedHistoryEntryId === entry.id
        return (
          <HistoryEntryItem
            key={entry.id}
            entry={entry}
            isDeployed={isDeployed}
            projectId={projectId}
            disabled={processing || isRewinding}
            onRewindingChange={onRewindingChange}
            t={t}
            language={i18n.language}
            entryTypeConfig={entryTypeConfig}
          />
        )
      })}

      {/* Pagination UI */}
      {historyPagy && historyPagy.pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('history.pagination.page')} <span className="font-medium">{historyPagy.page}</span> {t('history.pagination.of')}{' '}
            <span className="font-medium">{historyPagy.pages}</span>
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={historyPagy.page === 1}
              onClick={() => goToPage(1)}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!historyPagy.prev}
              onClick={() => goToPage(historyPagy.prev)}
            >
              {t('history.pagination.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!historyPagy.next}
              onClick={() => goToPage(historyPagy.next)}
            >
              {t('history.pagination.next')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={historyPagy.page === historyPagy.pages}
              onClick={() => goToPage(historyPagy.pages)}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
})

// History entry item (proactive progress log)
interface HistoryEntryItemProps {
  entry: HistoryEntry
  isDeployed?: boolean
  projectId: number
  disabled?: boolean
  onRewindingChange?: (isRewinding: boolean) => void
  t: TFunction
  language: string
  entryTypeConfig: ReturnType<typeof getEntryTypeConfig>
}

function HistoryEntryItem({
  entry,
  isDeployed = false,
  projectId,
  disabled = false,
  onRewindingChange,
  t,
  entryTypeConfig
}: HistoryEntryItemProps) {
  const [isThisRestoring, setIsThisRestoring] = useState(false)
  const isRestoringRef = useRef(false) // Ref guard for double-click prevention
  const config = (entry.entry_type ? entryTypeConfig[entry.entry_type] : undefined) ?? entryTypeConfig.new_feature
  const Icon = config.icon

  const isCurrent = entry.display_status === 'current'

  const handleRestore = async () => {
    // Use ref for immediate check (prevents race condition from async state updates)
    if (!entry.can_restore || isRestoringRef.current || disabled) return

    isRestoringRef.current = true
    setIsThisRestoring(true)
    onRewindingChange?.(true)

    try {
      await withProgressToast(
        {
          id: `rewind-${entry.id}`,
          loadingMessage: t('history.rewind.rewinding'),
          loadingDescription: t('history.rewind.restoringTo', { title: entry.title }),
          successMessage: t('history.rewind.success'),
        },
        () => restoreHistory(projectId, entry.id)
      )
    } catch {
      // Error already shown by withProgressToast
    } finally {
      isRestoringRef.current = false
      setIsThisRestoring(false)
      onRewindingChange?.(false)
    }
  }

  return (
    <div className={`relative rounded-lg p-4 transition-colors ${
      isDeployed ? 'border-2 border-emerald-400 bg-white dark:border-emerald-500 dark:bg-emerald-950/20' :
      isCurrent ? 'border-2 border-blue-400 bg-blue-50/50 dark:border-blue-500 dark:bg-blue-950/30' :
      'border border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/70'
    }`}>
      {/* Live badge - top right corner */}
      {isDeployed && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1 px-2.5 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full shadow-md">
          <Rocket className="h-3 w-3" />
          {t('history.badges.live')}
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {entry.title}
            </h4>
            <Badge variant="outline" className={`text-xs ${config.badgeClass}`}>
              {config.label}
            </Badge>
          </div>

          {entry.summary && (
            <p className="text-sm mb-3 leading-relaxed text-gray-700 dark:text-gray-300">
              {entry.summary}
            </p>
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(entry.created_at).toLocaleDateString(getDateLocale(), {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>

            {/* Rewind button with confirmation */}
            {entry.can_restore && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isThisRestoring || disabled}
                    className="text-xs"
                  >
                    <History className="h-3.5 w-3.5 mr-1" />
                    {isThisRestoring ? t('history.rewind.rewinding') : t('history.rewind.button')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('history.rewind.confirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-2">
                        <p>
                          {t('history.rewind.confirmDescription')}
                        </p>
                        <p className="text-amber-600 dark:text-amber-500 font-medium">
                          {t('history.rewind.confirmWarning')}
                        </p>
                        <p className="text-amber-600 dark:text-amber-500">
                          {t('history.rewind.confirmNote')}
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('history.rewind.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleRestore} disabled={isThisRestoring || disabled}>
                      {isThisRestoring ? t('history.rewind.rewinding') : t('history.rewind.button')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {/* Current badge */}
            {isCurrent && (
              <Badge variant="default" className="text-xs rounded-full bg-blue-500 text-white">
                {t('history.badges.current')}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
